import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Save, CheckCircle, Shield, User, Briefcase, Users, DollarSign, Car, PenLine, AlertCircle, Phone, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import SignatureCanvas from '../components/SignatureCanvas';
import { SharedNav } from '../components/SharedLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOTAL_STEPS = 13;

const stepConfig = [
  { id: 1,  icon: <Shield size={18} />,   en: 'Consent & Identity',    es: 'Consentimiento e Identidad' },
  { id: 2,  icon: <User size={18} />,     en: 'Your Personal Info',    es: 'Información Personal' },
  { id: 3,  icon: <User size={18} />,     en: 'About the Defendant',   es: 'Sobre el Acusado' },
  { id: 4,  icon: <Briefcase size={18} />, en: 'Your Employment',       es: 'Su Empleo' },
  { id: 5,  icon: <Users size={18} />,    en: 'Spouse / Partner',      es: 'Cónyuge / Pareja' },
  { id: 6,  icon: <Users size={18} />,    en: 'Children',              es: 'Hijos' },
  { id: 7,  icon: <Users size={18} />,    en: 'Family Contacts',       es: 'Contactos Familiares' },
  { id: 8,  icon: <Users size={18} />,    en: 'Brothers & Sisters',    es: 'Hermanos y Hermanas' },
  { id: 9,  icon: <Users size={18} />,    en: 'Other Relatives',       es: 'Otros Familiares' },
  { id: 10, icon: <Users size={18} />,    en: 'Personal References',   es: 'Referencias Personales' },
  { id: 11, icon: <DollarSign size={18} />, en: 'Financial Info',      es: 'Información Financiera' },
  { id: 12, icon: <Car size={18} />,      en: 'Vehicles',              es: 'Vehículos' },
  { id: 13, icon: <PenLine size={18} />,  en: 'Review & Sign',         es: 'Revisar y Firmar' },
];

// Field component
const Field = ({ label, name, value, onChange, type = 'text', placeholder = '', required = false, half = false, options = null }) => (
  <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
    <label className="form-label">{label}{required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}</label>
    {options ? (
      <select name={name} value={value || ''} onChange={onChange} className="form-input">
        <option value="">— Select —</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : (
      <input type={type} name={name} value={value || ''} onChange={onChange}
        className="form-input" placeholder={placeholder} required={required} />
    )}
  </div>
);

const SectionTitle = ({ children }) => (
  <div style={{ gridColumn: 'span 2', borderBottom: '1px solid rgba(255,107,0,0.2)', paddingBottom: '0.5rem', marginBottom: '0.25rem' }}>
    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--neon-orange)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</h3>
  </div>
);

// Repeating row group (siblings, refs, etc.)
const RepeaterGroup = ({ label, fields, items, onChange, count, lang }) => {
  const rows = Array.from({ length: count }).map((_, i) => items[i] || {});
  return (
    <div style={{ gridColumn: 'span 2' }}>
      <div style={{ borderBottom: '1px solid rgba(255,107,0,0.15)', paddingBottom: '0.4rem', marginBottom: '0.75rem' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      </div>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '0.75rem', padding: '0.85rem', background: 'rgba(255,255,255,0.02)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ gridColumn: 'span 3', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
            <span style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,107,0,0.15)', border: '1px solid rgba(255,107,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, color: 'var(--neon-orange)', flexShrink: 0 }}>{i+1}</span>
          </div>
          {fields.map(f => (
            <div key={f.key}>
              <label className="form-label">{lang === 'es' ? f.labelEs : f.labelEn}</label>
              <input className="form-input" value={row[f.key] || ''} onChange={e => {
                const updated = [...(items.length > i ? items : [...items, ...Array.from({ length: i + 1 - items.length }).map(() => ({}))])];
                while (updated.length <= i) updated.push({});
                updated[i] = { ...updated[i], [f.key]: e.target.value };
                onChange(updated);
              }} placeholder={lang === 'es' ? f.labelEs : f.labelEn} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const IntakePage = () => {
  const { lang } = useLanguage();
  const [phase, setPhase] = useState('landing'); // landing | resume | consent | form | success
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [ssn4, setSsn4] = useState('');
  const [dob, setDob]   = useState('');
  const [signature, setSignature] = useState(null);
  const [errors, setErrors]       = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formId, setFormId]        = useState(null);
  const [resumeData, setResumeData] = useState(null);

  const setField = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // Auto-save draft every 15s while filling
  useEffect(() => {
    if (phase !== 'form' || !ssn4 || !dob) return;
    const timer = setInterval(async () => {
      try {
        await axios.post(`${API}/drafts`, { ssn_last4: ssn4, dob, step, data: formData });
      } catch {}
    }, 15000);
    return () => clearInterval(timer);
  }, [phase, step, formData, ssn4, dob]);

  const handleResume = async () => {
    if (ssn4.length !== 4 || !dob) { setErrors({ resume: lang === 'es' ? 'Por favor ingresa los últimos 4 del SSN y fecha de nacimiento.' : 'Please enter last 4 of SSN and date of birth.' }); return; }
    try {
      const res = await axios.get(`${API}/drafts/resume`, { params: { ssn_last4: ssn4, dob } });
      if (res.data.found) {
        setResumeData(res.data.draft);
        setFormData(res.data.draft.data || {});
        setPhase('consent');
        toast.success(lang === 'es' ? '¡Solicitud encontrada! Continuando...' : 'Application found! Continuing...');
      } else {
        setPhase('consent');
        toast(lang === 'es' ? 'No se encontró solicitud guardada. Iniciando nueva.' : 'No saved application found. Starting new.');
      }
    } catch { setPhase('consent'); }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API}/drafts`, { ssn_last4: ssn4, dob, step, data: formData });
      toast.success(lang === 'es' ? 'Progreso guardado' : 'Progress saved');
    } catch { toast.error('Save failed'); }
  };

  const validate = () => {
    const errs = {};
    if (step === 2) {
      if (!formData.full_name) errs.full_name = true;
      if (!formData.telephone) errs.telephone = true;
    }
    if (step === 13 && !signature) errs.signature = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const nextStep = async () => {
    if (!validate()) { toast.error(lang === 'es' ? 'Por favor completa los campos requeridos.' : 'Please complete required fields.'); return; }
    if (step < TOTAL_STEPS) {
      setStep(s => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      await handleSave();
    }
  };

  const prevStep = () => { if (step > 1) { setStep(s => s - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); } };

  const handleSubmit = async () => {
    if (!signature) { toast.error(lang === 'es' ? 'Se requiere firma.' : 'Signature is required.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/submissions`, {
        ssn_last4: ssn4,
        dob,
        data: formData,
        client_signature: signature,
      });
      setFormId(res.data.form_id);
      setPhase('success');
      toast.success(lang === 'es' ? '¡Formulario enviado!' : 'Form submitted!');
    } catch (e) {
      toast.error(lang === 'es' ? 'Error al enviar. Intente de nuevo.' : 'Submission failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  const progress = ((step - 1) / (TOTAL_STEPS - 1)) * 100;

  // ── LANDING ───────────────────────────────────────────────
  if (phase === 'landing') {
    return (
      <div style={{ background: 'var(--bg-0)', minHeight: '100vh' }}>
        <Helmet>
          <meta name="robots" content="noindex,nofollow,noarchive" />
          <title>Intake - Lone Star Bonding Co.</title>
        </Helmet>
        <SharedNav />
        <section style={{ paddingTop: '8rem', paddingBottom: '4rem', background: 'var(--bg-1)', borderBottom: '1px solid var(--border-steel)', textAlign: 'center' }}>
          <div className="container" style={{ maxWidth: 700 }}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.25)', borderRadius: 99, padding: '0.3rem 1rem', marginBottom: '1.5rem' }}>
                <Shield size={13} color="var(--neon-orange)" />
                <span style={{ fontSize: '0.75rem', color: 'var(--neon-orange)', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  {lang === 'es' ? 'SEGURO — CONFIDENCIAL — GUIADO' : 'SECURE — CONFIDENTIAL — GUIDED'}
                </span>
              </div>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', textTransform: 'uppercase', marginBottom: '1rem', lineHeight: 1.05 }}>
                {lang === 'es' ? 'Formularios de Solicitud de Fianza' : 'Bond Application Forms'}
              </h1>
              <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)', marginBottom: '2.5rem', lineHeight: 1.7 }}>
                {lang === 'es'
                  ? 'Un cuestionario guiado paso a paso. Su información se ingresa una vez y se distribuye automáticamente a los formularios correctos.'
                  : 'One guided step-by-step questionnaire. Your information is entered once and automatically distributed to the correct forms.'}
              </p>
              {/* Options */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
                <div style={{ background: 'var(--surface-1)', border: '1px solid rgba(255,107,0,0.25)', borderRadius: 12, padding: '2rem', textAlign: 'left' }}>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', textTransform: 'uppercase', marginBottom: '0.75rem', color: 'var(--neon-orange)' }}>
                    {lang === 'es' ? 'CONTINUAR SOLICITUD' : 'CONTINUE APPLICATION'}
                  </h3>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.25rem', lineHeight: 1.55 }}>
                    {lang === 'es' ? 'Ingresa los últimos 4 del SSN y fecha de nacimiento para continuar.' : 'Enter your last 4 SSN and date of birth to resume.'}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', marginBottom: '1rem' }}>
                    <div>
                      <label className="form-label">{lang === 'es' ? 'Últimos 4 del SSN' : 'Last 4 of SSN'}</label>
                      <input className="form-input" maxLength={4} value={ssn4} onChange={e => setSsn4(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 1234" data-testid="ssn-input" />
                    </div>
                    <div>
                      <label className="form-label">{lang === 'es' ? 'Fecha de Nacimiento' : 'Date of Birth'}</label>
                      <input className="form-input" type="date" value={dob} onChange={e => setDob(e.target.value)} data-testid="dob-input" />
                    </div>
                    {errors.resume && <p className="form-error">{errors.resume}</p>}
                  </div>
                  <button onClick={handleResume} className="btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: '0.9rem' }} data-testid="resume-btn">
                    {lang === 'es' ? 'CONTINUAR MI SOLICITUD' : 'RESUME MY APPLICATION'}
                  </button>
                </div>

                <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border-steel)', borderRadius: 12, padding: '2rem', textAlign: 'left' }}>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                    {lang === 'es' ? 'NUEVA SOLICITUD' : 'NEW APPLICATION'}
                  </h3>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.25rem', lineHeight: 1.55 }}>
                    {lang === 'es' ? 'Inicia el proceso guiado desde el principio. Tomará aproximadamente 15–20 minutos.' : 'Start the guided process from the beginning. Takes approximately 15–20 minutes.'}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
                    {['Personal References', 'Surety Bond Application', 'Asset & Collateral Inventory'].map((f, i) => (
                      <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                        <CheckCircle size={13} color="var(--success)" />
                        {f}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', marginBottom: '1rem' }}>
                    <div>
                      <label className="form-label">{lang === 'es' ? 'Sus Últimos 4 del SSN' : 'Your Last 4 of SSN'}</label>
                      <input className="form-input" maxLength={4} value={ssn4} onChange={e => setSsn4(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 1234" />
                    </div>
                    <div>
                      <label className="form-label">{lang === 'es' ? 'Fecha de Nacimiento' : 'Date of Birth'}</label>
                      <input className="form-input" type="date" value={dob} onChange={e => setDob(e.target.value)} />
                    </div>
                  </div>
                  <button onClick={() => { if (!ssn4 || ssn4.length < 4 || !dob) { setErrors({ resume: lang === 'es' ? 'Requerido' : 'Required' }); return; } setPhase('consent'); }}
                    className="btn-outline" style={{ width: '100%', justifyContent: 'center', fontSize: '0.9rem' }} data-testid="new-app-btn">
                    {lang === 'es' ? 'INICIAR NUEVA SOLICITUD' : 'START NEW APPLICATION'} <ArrowRight size={14} />
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'rgba(46,229,157,0.06)', border: '1px solid rgba(46,229,157,0.15)', borderRadius: 8, padding: '0.85rem 1.25rem', fontSize: '0.82rem', color: 'var(--text-muted)', textAlign: 'left' }}>
                <Shield size={16} color="var(--success)" style={{ flexShrink: 0 }} />
                {lang === 'es' ? 'Sus datos están protegidos por el Módulo de Seguridad TITN Aegis — encriptación personalizada de extremo a extremo desarrollada por James Hudson @ Texas IT Nerds.' : 'Your data is protected by the TITN Aegis Security Module — custom end-to-end encryption developed by James Hudson @ Texas IT Nerds.'}
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  // ── CONSENT ───────────────────────────────────────────────
  if (phase === 'consent') {
    return (
      <div style={{ background: 'var(--bg-0)', minHeight: '100vh' }}>
        <SharedNav />
        <section style={{ paddingTop: '8rem', paddingBottom: '4rem', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
          <div className="container" style={{ maxWidth: 680 }}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              style={{ background: 'var(--surface-1)', border: '1px solid rgba(255,107,0,0.2)', borderRadius: 16, padding: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ width: 50, height: 50, borderRadius: 12, background: 'rgba(255,107,0,0.12)', border: '1px solid rgba(255,107,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Shield size={24} color="var(--neon-orange)" />
                </div>
                <div>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', textTransform: 'uppercase' }}>
                    {lang === 'es' ? 'CONSENTIMIENTO DE DATOS Y SEGURIDAD' : 'DATA CONSENT & SECURITY AGREEMENT'}
                  </h2>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
                    {lang === 'es' ? 'Lea antes de continuar' : 'Please read before continuing'}
                  </p>
                </div>
              </div>

              <div style={{ background: 'var(--bg-2)', borderRadius: 10, padding: '1.5rem', marginBottom: '1.5rem', border: '1px solid var(--border-steel)', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.75 }}>
                <p style={{ marginBottom: '1rem' }}>
                  {lang === 'es'
                    ? 'Al continuar, usted consiente que Lone Star Bonding Co. almacene de forma segura su información personal en nuestra plataforma. Todos los datos están protegidos por el '
                    : 'By proceeding, you consent to Lone Star Bonding Co. securely storing your personal information on our platform. All data is protected by the '}
                  <strong style={{ color: 'var(--neon-orange)' }}>TITN Aegis Security Module</strong>
                  {lang === 'es'
                    ? ' — un sistema de seguridad personalizado, completamente encriptado, diseñado y desarrollado internamente desde cero por James Hudson en Texas IT Nerds.'
                    : ' — a custom-developed, fully encrypted security system designed and built in-house from the ground up by James Hudson at Texas IT Nerds.'}
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  {lang === 'es'
                    ? 'Su información está encriptada en reposo y en tránsito, y solo es accesible al personal autorizado de Lone Star Bonding Co.'
                    : 'Your information is encrypted at rest and in transit, accessible only to authorized Lone Star Bonding Co. personnel.'}
                </p>
                <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  {lang === 'es'
                    ? 'Esta información se utilizará exclusivamente para procesar su solicitud de fianza. Lone Star Bonding Co. | Licencia No. 74629'
                    : 'This information will be used solely to process your bail bond application. Lone Star Bonding Co. | License No. 74629'}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button onClick={() => { setStep(1); setPhase('form'); }}
                  className="btn-primary" style={{ flex: 1, justifyContent: 'center', fontSize: '0.95rem', fontFamily: 'var(--font-display)', letterSpacing: '0.06em' }} data-testid="consent-agree-btn">
                  <CheckCircle size={16} />
                  {lang === 'es' ? 'ACEPTO — CONTINUAR' : 'I AGREE — PROCEED SECURELY'}
                </button>
                <button onClick={() => setPhase('landing')} className="btn-outline" style={{ fontSize: '0.88rem' }}>
                  <X size={14} /> {lang === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  // ── SUCCESS ───────────────────────────────────────────────
  if (phase === 'success') {
    return (
      <div style={{ background: 'var(--bg-0)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <Helmet>
          <meta name="robots" content="noindex,nofollow,noarchive" />
        </Helmet>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}
          style={{ background: 'var(--surface-1)', border: '1px solid rgba(46,229,157,0.3)', borderRadius: 16, padding: '3rem', maxWidth: 560, width: '100%', textAlign: 'center' }}>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(46,229,157,0.15)', border: '2px solid var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
            <CheckCircle size={38} color="var(--success)" />
          </motion.div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', textTransform: 'uppercase', marginBottom: '0.75rem', color: 'var(--success)' }}>
            {lang === 'es' ? '¡ENVIADO CON ÉXITO!' : 'SUBMITTED SUCCESSFULLY!'}
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem', lineHeight: 1.7 }}>
            {lang === 'es' ? 'Sus formularios han sido recibidos de forma segura por Lone Star Bonding Co.' : 'Your forms have been securely received by Lone Star Bonding Co.'}
          </p>
          {formId && (
            <div style={{ background: 'var(--bg-2)', borderRadius: 8, padding: '0.75rem 1rem', display: 'inline-block', marginBottom: '1.5rem' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{lang === 'es' ? 'ID de Formulario: ' : 'Form ID: '}</span>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--neon-orange)', fontWeight: 700 }}>{formId}</span>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
              {lang === 'es'
                ? 'El equipo de Lone Star Bonding Co. revisará su solicitud y se comunicará con usted próximamente. Para ayuda inmediata, llame ahora.'
                : 'The Lone Star Bonding Co. team will review your submission and contact you shortly. For immediate help, call now.'}
            </p>
            <a href="tel:7132288800" className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center', textDecoration: 'none', fontSize: '1rem', fontFamily: 'var(--font-display)', letterSpacing: '0.08em', padding: '0.9rem' }}>
              <Phone size={18} /> {lang === 'es' ? 'LLAMAR (713) 228-8800' : 'CALL (713) 228-8800'}
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  // ── FORM ──────────────────────────────────────────────────
  const renderStep = () => {
    const gridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' };
    const gridFullStyle = { display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' };

    switch (step) {
      // ── Step 1: Already handled by consent. Step 1 in form is recap. ──
      case 1:
        return (
          <div style={gridStyle}>
            <SectionTitle>{lang === 'es' ? 'Identificación del Declarante' : 'Your Identification (for form resume)'}</SectionTitle>
            <div style={{ gridColumn: 'span 2', background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.15)', borderRadius: 8, padding: '1rem', fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.65 }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <AlertCircle size={16} color="var(--neon-orange)" style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{lang === 'es' ? 'Sus últimos 4 del SSN y fecha de nacimiento son su clave de acceso para continuar esta solicitud más tarde si necesita parar y regresar.' : 'Your last 4 SSN and date of birth are your access key to continue this application later if you need to stop and return.'}</span>
              </div>
            </div>
            <div>
              <label className="form-label">{lang === 'es' ? 'Sus últimos 4 del SSN' : 'Your Last 4 of SSN'} <span style={{ color: 'var(--danger)' }}>*</span></label>
              <input className="form-input" value={ssn4} onChange={e => setSsn4(e.target.value.replace(/\D/g,''))} maxLength={4} placeholder="XXXX" readOnly style={{ background: 'rgba(255,107,0,0.04)' }} />
            </div>
            <div>
              <label className="form-label">{lang === 'es' ? 'Fecha de Nacimiento' : 'Date of Birth'} <span style={{ color: 'var(--danger)' }}>*</span></label>
              <input className="form-input" type="date" value={dob} onChange={e => setDob(e.target.value)} readOnly style={{ background: 'rgba(255,107,0,0.04)' }} />
            </div>
          </div>
        );

      // ── Step 2: Personal Info ──
      case 2:
        return (
          <div style={gridStyle}>
            <SectionTitle>{lang === 'es' ? 'Información Personal del Indemnizador' : 'Indemnitor Personal Information'}</SectionTitle>
            <Field label={lang === 'es' ? 'Nombre Completo (Apellido, Nombre, Inicial)' : 'Full Name (Last, First, MI)'} name="full_name" value={formData.full_name} onChange={handleChange} required placeholder="Last, First MI" />
            <Field label={lang === 'es' ? 'Apodo / Alias' : 'Nickname / Alias'} name="nickname_alias" value={formData.nickname_alias} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Calle / Número' : 'Street Address'} name="address_street" value={formData.address_street} onChange={handleChange} placeholder="123 Main St" />
            <Field label={lang === 'es' ? 'Apt / Suite' : 'Apt / Suite'} name="address_apt" value={formData.address_apt} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Ciudad' : 'City'} name="address_city" value={formData.address_city} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Estado' : 'State'} name="address_state" value={formData.address_state} onChange={handleChange} half />
            <Field label="ZIP" name="address_zip" value={formData.address_zip} onChange={handleChange} half />
            <Field label={lang === 'es' ? '¿Cuánto tiempo en esta dirección?' : 'How Long at This Address?'} name="how_long_at_address" value={formData.how_long_at_address} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Hipotecaria / Dueño / Arrendatario' : 'Mortgage Co. / Landlord'} name="mortgage_co_landlord" value={formData.mortgage_co_landlord} onChange={handleChange} />
            <Field label={lang === 'es' ? '¿Cuánto tiempo?' : 'How Long?'} name="mortgage_how_long" value={formData.mortgage_how_long} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Dirección Anterior' : 'Previous Address'} name="previous_address" value={formData.previous_address} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Teléfono / Celular' : 'Phone / Cell'} name="telephone" value={formData.telephone} onChange={handleChange} required type="tel" placeholder="(713) 555-0000" half />
            <Field label="Email" name="email" value={formData.email} onChange={handleChange} type="email" half />
            <Field label={lang === 'es' ? "Licencia de Conducir / ID #" : "Driver's License / ID #"} name="drivers_license" value={formData.drivers_license} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Otro ID' : 'Other ID'} name="other_id" value={formData.other_id} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Número de Seguro Social' : 'Social Security #'} name="ssn" value={formData.ssn} onChange={handleChange} type="password" placeholder="XXX-XX-XXXX" half />
            <Field label={lang === 'es' ? 'Fecha de Nacimiento' : 'Date of Birth'} name="dob" value={formData.dob || dob} onChange={handleChange} type="date" half />
            <Field label={lang === 'es' ? 'Edad' : 'Age'} name="age" value={formData.age} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Sexo' : 'Sex'} name="sex" value={formData.sex} onChange={handleChange} half options={[{value:'M',label:'Male / Masculino'},{value:'F',label:'Female / Femenino'},{value:'Other',label:'Other'}]} />
            <Field label={lang === 'es' ? 'Peso' : 'Weight'} name="weight" value={formData.weight} onChange={handleChange} half placeholder="e.g. 175 lbs" />
            <Field label={lang === 'es' ? 'Estatura' : 'Height'} name="height" value={formData.height} onChange={handleChange} half placeholder="e.g. 5ft 10in" />
            <Field label={lang === 'es' ? 'Complexión' : 'Build'} name="build" value={formData.build} onChange={handleChange} half options={[{value:'Slim',label:'Slim'},{value:'Medium',label:'Medium'},{value:'Heavy',label:'Heavy'},{value:'Athletic',label:'Athletic'}]} />
            <Field label={lang === 'es' ? 'Raza' : 'Race'} name="race" value={formData.race} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Color de Cabello' : 'Hair Color'} name="hair_color" value={formData.hair_color} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Color de Ojos' : 'Eye Color'} name="eye_color" value={formData.eye_color} onChange={handleChange} half />
          </div>
        );

      // ── Step 3: Defendant ──
      case 3:
        return (
          <div style={gridStyle}>
            <SectionTitle>{lang === 'es' ? 'Información del Acusado' : 'About the Defendant'}</SectionTitle>
            <Field label={lang === 'es' ? 'Nombre del Acusado' : "Defendant's Full Name"} name="defendant_name" value={formData.defendant_name} onChange={handleChange} required />
            <Field label={lang === 'es' ? 'Relación con el Acusado' : 'Your Relation to Defendant'} name="relation_to_defendant" value={formData.relation_to_defendant} onChange={handleChange} required half options={[{value:'Spouse',label:'Spouse'},{value:'Parent',label:'Parent'},{value:'Sibling',label:'Sibling'},{value:'Child',label:'Child'},{value:'Friend',label:'Friend'},{value:'Other',label:'Other'}]} />
            <Field label={lang === 'es' ? 'Teléfono del Acusado' : "Defendant's Phone"} name="defendant_phone" value={formData.defendant_phone} onChange={handleChange} type="tel" half />
            <Field label={lang === 'es' ? 'Dirección del Acusado' : "Defendant's Address"} name="defendant_address" value={formData.defendant_address} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Cargo / Delito' : 'Charge / Offense'} name="charge" value={formData.charge} onChange={handleChange} />
            <Field label="SPN #" name="spn" value={formData.spn} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Monto de la Fianza' : 'Bond Amount'} name="bond_amount" value={formData.bond_amount} onChange={handleChange} half placeholder="e.g. $25,000" />
            <Field label={lang === 'es' ? 'Número de Causa / Caso' : 'Cause / Case Number'} name="cause_number" value={formData.cause_number} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Nombre del Tribunal' : 'Court Name'} name="court_name" value={formData.court_name} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Agencia de Arresto' : 'Arresting Agency'} name="arresting_agency" value={formData.arresting_agency} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Cárcel / Instalación' : 'Jail / Facility'} name="jail_facility" value={formData.jail_facility} onChange={handleChange} half />
          </div>
        );

      // ── Step 4: Employment ──
      case 4:
        return (
          <div style={gridStyle}>
            <SectionTitle>{lang === 'es' ? 'Empleo Actual' : 'Current Employment'}</SectionTitle>
            <Field label={lang === 'es' ? 'Empleador' : 'Employer'} name="employer" value={formData.employer} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Ocupación' : 'Occupation'} name="occupation" value={formData.occupation} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Salario Mensual' : 'Monthly Salary'} name="monthly_salary" value={formData.monthly_salary} onChange={handleChange} half placeholder="$0,000" />
            <Field label={lang === 'es' ? 'Dirección del Empleador' : 'Employer Address'} name="employer_address" value={formData.employer_address} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Teléfono del Empleador' : 'Employer Phone'} name="employer_phone" value={formData.employer_phone} onChange={handleChange} type="tel" half />
            <Field label={lang === 'es' ? '¿Cuánto tiempo?' : 'How Long?'} name="employer_how_long" value={formData.employer_how_long} onChange={handleChange} half />
            <SectionTitle>{lang === 'es' ? 'Empleo Anterior' : 'Former Employment'}</SectionTitle>
            <Field label={lang === 'es' ? 'Empleador Anterior' : 'Former Employer'} name="former_employer" value={formData.former_employer} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Ocupación' : 'Occupation'} name="former_occupation" value={formData.former_occupation} onChange={handleChange} half />
            <Field label={lang === 'es' ? '¿Cuánto tiempo?' : 'How Long?'} name="former_how_long" value={formData.former_how_long} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Dirección Anterior del Empleador' : 'Former Employer Address'} name="former_employer_address" value={formData.former_employer_address} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Teléfono' : 'Phone'} name="former_employer_phone" value={formData.former_employer_phone} onChange={handleChange} type="tel" half />
          </div>
        );

      // ── Step 5: Spouse ──
      case 5:
        return (
          <div style={gridStyle}>
            <SectionTitle>{lang === 'es' ? 'Empleo del Cónyuge / Pareja' : 'Spouse / Common Law Employment'}</SectionTitle>
            <Field label={lang === 'es' ? 'Nombre del Cónyuge / Pareja' : "Spouse / Common Law's Name"} name="spouse_name" value={formData.spouse_name} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Dirección del Cónyuge' : "Spouse's Address"} name="spouse_address" value={formData.spouse_address} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Empleador' : 'Employer'} name="spouse_employer" value={formData.spouse_employer} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Ocupación' : 'Occupation'} name="spouse_occupation" value={formData.spouse_occupation} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Dirección del Empleador' : 'Employer Address'} name="spouse_employer_address" value={formData.spouse_employer_address} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Teléfono' : 'Phone'} name="spouse_employer_phone" value={formData.spouse_employer_phone} onChange={handleChange} type="tel" half />
            <Field label={lang === 'es' ? '¿Cuánto tiempo?' : 'How Long?'} name="spouse_employer_how_long" value={formData.spouse_employer_how_long} onChange={handleChange} half />
          </div>
        );

      // ── Step 6: Children ──
      case 6:
        return (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <SectionTitle>{lang === 'es' ? 'Nombres de los Hijos y Escuelas' : "Children's Names and Schools Attended"}</SectionTitle>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: 8, padding: '1rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ gridColumn: 'span 3', fontSize: '0.78rem', color: 'var(--neon-orange)', fontWeight: 600, textTransform: 'uppercase' }}>{lang === 'es' ? `Hijo ${i + 1}` : `Child ${i + 1}`}</span>
                <div><label className="form-label">{lang === 'es' ? 'Nombre' : 'Name'}</label><input className="form-input" value={(formData.children || [])[i]?.name || ''} onChange={e => { const c = [...(formData.children || [0,1,2].map(() => ({})))]; while(c.length <= i) c.push({}); c[i] = { ...c[i], name: e.target.value }; setField('children', c); }} /></div>
                <div><label className="form-label">{lang === 'es' ? 'Edad' : 'Age'}</label><input className="form-input" value={(formData.children || [])[i]?.age || ''} onChange={e => { const c = [...(formData.children || [0,1,2].map(() => ({})))]; while(c.length <= i) c.push({}); c[i] = { ...c[i], age: e.target.value }; setField('children', c); }} /></div>
                <div><label className="form-label">{lang === 'es' ? 'Escuela' : 'School'}</label><input className="form-input" value={(formData.children || [])[i]?.school || ''} onChange={e => { const c = [...(formData.children || [0,1,2].map(() => ({})))]; while(c.length <= i) c.push({}); c[i] = { ...c[i], school: e.target.value }; setField('children', c); }} /></div>
              </div>
            ))}
          </div>
        );

      // ── Step 7: Family Contacts ──
      case 7:
        return (
          <div style={{ display: 'grid', gap: '1.25rem' }}>
            <SectionTitle>{lang === 'es' ? 'Padres / Referencias Personales Familiares' : "Parent's / Personal Family References"}</SectionTitle>
            {[
              { en: 'Mother', es: 'Madre', prefix: 'mother' },
              { en: 'Father', es: 'Padre', prefix: 'father' },
              { en: 'Mother-In-Law', es: 'Suegra', prefix: 'mother_in_law' },
              { en: 'Father-In-Law', es: 'Suegro', prefix: 'father_in_law' },
            ].map(({ en, es: esLabel, prefix }) => (
              <div key={prefix} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: 8, padding: '1rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ gridColumn: 'span 3', fontSize: '0.78rem', color: 'var(--neon-orange)', fontWeight: 600, textTransform: 'uppercase' }}>{lang === 'es' ? esLabel : en}</span>
                <div><label className="form-label">{lang === 'es' ? 'Nombre' : 'Name'}</label><input className="form-input" value={formData[`${prefix}_name`] || ''} onChange={e => setField(`${prefix}_name`, e.target.value)} /></div>
                <div><label className="form-label">{lang === 'es' ? 'Teléfono' : 'Phone #'}</label><input className="form-input" type="tel" value={formData[`${prefix}_phone`] || ''} onChange={e => setField(`${prefix}_phone`, e.target.value)} /></div>
                <div><label className="form-label">{lang === 'es' ? 'Dirección' : 'Address'}</label><input className="form-input" value={formData[`${prefix}_address`] || ''} onChange={e => setField(`${prefix}_address`, e.target.value)} /></div>
              </div>
            ))}
          </div>
        );

      // ── Step 8: Siblings ──
      case 8:
        return (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <RepeaterGroup
              label={lang === 'es' ? 'Hermanos y Hermanas (en el estado o fuera del estado)' : 'Brothers & Sisters (In State or Out of State)'}
              fields={[
                { key: 'name', labelEn: 'Name', labelEs: 'Nombre' },
                { key: 'phone', labelEn: 'Phone #', labelEs: 'Teléfono' },
                { key: 'address', labelEn: 'Address', labelEs: 'Dirección' },
              ]}
              items={formData.siblings || []}
              onChange={val => setField('siblings', val)}
              count={4}
              lang={lang}
            />
          </div>
        );

      // ── Step 9: Other Relatives ──
      case 9:
        return (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <RepeaterGroup
              label={lang === 'es' ? 'Otros Familiares (en el estado o fuera del estado)' : 'Other Relatives (In State or Out of State)'}
              fields={[
                { key: 'name', labelEn: 'Name', labelEs: 'Nombre' },
                { key: 'phone', labelEn: 'Phone #', labelEs: 'Teléfono' },
                { key: 'address', labelEn: 'Address', labelEs: 'Dirección' },
              ]}
              items={formData.relatives || []}
              onChange={val => setField('relatives', val)}
              count={4}
              lang={lang}
            />
          </div>
        );

      // ── Step 10: Personal References ──
      case 10:
        return (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div style={{ background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.15)', borderRadius: 8, padding: '0.85rem', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              ℹ️ {lang === 'es' ? 'Estas referencias se usarán en el Formulario de Referencias Personales Y en la Solicitud de Fianza. Ingrese amigos cercanos, compañeros de trabajo, etc. — no familiares.' : 'These references populate both the Personal Reference Form AND the Surety Bond Application. List close friends, coworkers, etc. — not family.'}
            </div>
            <RepeaterGroup
              label={lang === 'es' ? 'Referencias Personales (Amigos, Compañeros de Trabajo, etc.) — 5 requeridas' : 'Personal References (Close Friends, Co-Workers, Teammates) — 5 required'}
              fields={[
                { key: 'name', labelEn: 'Full Name', labelEs: 'Nombre Completo' },
                { key: 'address', labelEn: 'Address', labelEs: 'Dirección' },
                { key: 'phone', labelEn: 'Phone #', labelEs: 'Teléfono' },
              ]}
              items={formData.references || []}
              onChange={val => setField('references', val)}
              count={5}
              lang={lang}
            />
          </div>
        );

      // ── Step 11: Financial ──
      case 11:
        return (
          <div style={{ display: 'grid', gap: '1.25rem' }}>
            <SectionTitle>{lang === 'es' ? 'Tarjetas de Crédito' : 'Credit Cards'}</SectionTitle>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: 8, padding: '1rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ gridColumn: 'span 3', fontSize: '0.78rem', color: 'var(--neon-orange)', fontWeight: 600, textTransform: 'uppercase' }}>{`Card ${i + 1}`}</span>
                {[{k:'name',en:'Card Name',es:'Nombre'},{k:'number',en:'Card #',es:'Número'},{k:'exp',en:'Exp Date',es:'Vencimiento'}].map(f => (
                  <div key={f.k}><label className="form-label">{lang === 'es' ? f.es : f.en}</label>
                    <input className="form-input" value={(formData.credit_cards || [])[i]?.[f.k] || ''} onChange={e => { const cc = [...(formData.credit_cards || [{},{},{}])]; while(cc.length <= i) cc.push({}); cc[i] = { ...cc[i], [f.k]: e.target.value }; setField('credit_cards', cc); }} /></div>
                ))}
              </div>
            ))}
            <SectionTitle>{lang === 'es' ? 'Información Bancaria' : 'Banking Information'}</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div><label className="form-label">{lang === 'es' ? 'Nombre del Banco' : 'Bank Name'}</label><input className="form-input" value={formData.bank_name || ''} onChange={e => setField('bank_name', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Dirección / Sucursal' : 'Address / Branch'}</label><input className="form-input" value={formData.bank_address || ''} onChange={e => setField('bank_address', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Cuenta de Ahorros #' : 'Savings Account #'}</label><input className="form-input" value={formData.savings_account || ''} onChange={e => setField('savings_account', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Cuenta Corriente #' : 'Checking Account #'}</label><input className="form-input" value={formData.checking_account || ''} onChange={e => setField('checking_account', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Cooperativa de Crédito' : 'Credit Union'}</label><input className="form-input" value={formData.credit_union || ''} onChange={e => setField('credit_union', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Cuenta #' : 'Account #'}</label><input className="form-input" value={formData.credit_union_account || ''} onChange={e => setField('credit_union_account', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Acciones / Fondos Mutuos' : 'Stocks / Mutual Funds'}</label><input className="form-input" value={formData.stocks_mutual_funds || ''} onChange={e => setField('stocks_mutual_funds', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Cuenta #' : 'Account #'}</label><input className="form-input" value={formData.stocks_account || ''} onChange={e => setField('stocks_account', e.target.value)} /></div>
            </div>

            <SectionTitle>{lang === 'es' ? 'Bienes Raíces / Propiedad' : 'Real Property / Real Estate'}</SectionTitle>
            <div style={{ background: 'rgba(255,107,0,0.04)', border: '1px solid rgba(255,107,0,0.12)', borderRadius: 8, padding: '0.75rem', fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
              {lang === 'es' ? 'Enumere cualquier propiedad inmueble que posea (casa, terreno, etc.). Esto se usa para el formulario de inventario de activos/colateral.' : 'List any real property you own (home, land, etc.). This is used for the asset/collateral inventory form.'}
            </div>
            {[0, 1].map(i => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: 8, padding: '1rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ gridColumn: 'span 2', fontSize: '0.78rem', color: 'var(--neon-orange)', fontWeight: 600, textTransform: 'uppercase' }}>{lang === 'es' ? `Propiedad ${i + 1}` : `Property ${i + 1}`}</span>
                {[
                  {k:'address',en:'Property Address',es:'Dirección de la Propiedad'},
                  {k:'type',en:'Type (Home/Land/Commercial)',es:'Tipo (Casa/Terreno/Comercial)'},
                  {k:'estimated_value',en:'Estimated Value ($)',es:'Valor Estimado ($)'},
                  {k:'mortgage_balance',en:'Mortgage Balance Owed ($)',es:'Saldo de Hipoteca ($)'},
                  {k:'equity',en:'Estimated Equity ($)',es:'Capital Estimado ($)'},
                  {k:'mortgage_company',en:'Mortgage Company',es:'Compañía Hipotecaria'},
                ].map(f => (
                  <div key={f.k}><label className="form-label">{lang === 'es' ? f.es : f.en}</label>
                    <input className="form-input" value={(formData.real_property || [])[i]?.[f.k] || ''} onChange={e => { const rp = [...(formData.real_property || [{},{}])]; while(rp.length <= i) rp.push({}); rp[i] = { ...rp[i], [f.k]: e.target.value }; setField('real_property', rp); }} /></div>
                ))}
              </div>
            ))}

            <SectionTitle>{lang === 'es' ? 'Ingresos y Gastos Mensuales' : 'Monthly Income & Expenses'}</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div><label className="form-label">{lang === 'es' ? 'Ingreso Mensual Total ($)' : 'Total Monthly Income ($)'}</label><input className="form-input" value={formData.monthly_income || ''} onChange={e => setField('monthly_income', e.target.value)} placeholder="$0,000" /></div>
              <div><label className="form-label">{lang === 'es' ? 'Ingreso Mensual del Cónyuge ($)' : 'Spouse Monthly Income ($)'}</label><input className="form-input" value={formData.spouse_monthly_income || ''} onChange={e => setField('spouse_monthly_income', e.target.value)} placeholder="$0,000" /></div>
              <div><label className="form-label">{lang === 'es' ? 'Pago de Renta / Hipoteca ($)' : 'Rent / Mortgage Payment ($)'}</label><input className="form-input" value={formData.monthly_rent_mortgage || ''} onChange={e => setField('monthly_rent_mortgage', e.target.value)} placeholder="$0,000" /></div>
              <div><label className="form-label">{lang === 'es' ? 'Pagos de Auto ($)' : 'Car Payments ($)'}</label><input className="form-input" value={formData.monthly_car_payments || ''} onChange={e => setField('monthly_car_payments', e.target.value)} placeholder="$0,000" /></div>
              <div><label className="form-label">{lang === 'es' ? 'Servicios (Luz, Agua, Gas) ($)' : 'Utilities ($)'}</label><input className="form-input" value={formData.monthly_utilities || ''} onChange={e => setField('monthly_utilities', e.target.value)} placeholder="$0,000" /></div>
              <div><label className="form-label">{lang === 'es' ? 'Otros Gastos Mensuales ($)' : 'Other Monthly Expenses ($)'}</label><input className="form-input" value={formData.monthly_other_expenses || ''} onChange={e => setField('monthly_other_expenses', e.target.value)} placeholder="$0,000" /></div>
            </div>

            <SectionTitle>{lang === 'es' ? 'Deudas Pendientes' : 'Outstanding Debts'}</SectionTitle>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: 8, padding: '1rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ gridColumn: 'span 3', fontSize: '0.78rem', color: 'var(--neon-orange)', fontWeight: 600, textTransform: 'uppercase' }}>{lang === 'es' ? `Deuda ${i + 1}` : `Debt ${i + 1}`}</span>
                {[
                  {k:'creditor',en:'Creditor Name',es:'Nombre del Acreedor'},
                  {k:'amount_owed',en:'Amount Owed ($)',es:'Monto Adeudado ($)'},
                  {k:'monthly_payment',en:'Monthly Payment ($)',es:'Pago Mensual ($)'},
                ].map(f => (
                  <div key={f.k}><label className="form-label">{lang === 'es' ? f.es : f.en}</label>
                    <input className="form-input" value={(formData.outstanding_debts || [])[i]?.[f.k] || ''} onChange={e => { const d = [...(formData.outstanding_debts || [{},{},{}])]; while(d.length <= i) d.push({}); d[i] = { ...d[i], [f.k]: e.target.value }; setField('outstanding_debts', d); }} /></div>
                ))}
              </div>
            ))}

            <SectionTitle>{lang === 'es' ? 'Otra Propiedad Valiosa' : 'Other Valuable Property'}</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div style={{ gridColumn: 'span 2' }}><label className="form-label">{lang === 'es' ? 'Describa cualquier otra propiedad valiosa (joyas, electrónicos, herramientas, equipo, etc.)' : 'Describe any other valuable property (jewelry, electronics, tools, equipment, etc.)'}</label>
                <textarea className="form-input" rows={3} value={formData.other_valuable_property || ''} onChange={e => setField('other_valuable_property', e.target.value)} style={{ resize: 'vertical' }} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Valor Estimado Total ($)' : 'Estimated Total Value ($)'}</label><input className="form-input" value={formData.other_property_value || ''} onChange={e => setField('other_property_value', e.target.value)} placeholder="$0,000" /></div>
            </div>
          </div>
        );

      // ── Step 12: Automobiles ──
      case 12:
        return (
          <div style={{ display: 'grid', gap: '1.25rem' }}>
            <SectionTitle>{lang === 'es' ? 'Lista de Vehículos' : 'List All Automobiles'}</SectionTitle>
            {[0, 1].map(i => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: 8, padding: '1rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ gridColumn: 'span 5', fontSize: '0.78rem', color: 'var(--neon-orange)', fontWeight: 600, textTransform: 'uppercase' }}>{lang === 'es' ? `Vehículo ${i + 1}` : `Vehicle ${i + 1}`}</span>
                {[{k:'year',en:'Year',es:'Año'},{k:'make',en:'Make',es:'Marca'},{k:'model',en:'Model',es:'Modelo'},{k:'color',en:'Color',es:'Color'},{k:'license',en:'Lic. #',es:'Placa #'}].map(f => (
                  <div key={f.k}><label className="form-label">{lang === 'es' ? f.es : f.en}</label>
                    <input className="form-input" value={(formData.automobiles || [])[i]?.[f.k] || ''} onChange={e => { const a = [...(formData.automobiles || [{},{}])]; while(a.length <= i) a.push({}); a[i] = { ...a[i], [f.k]: e.target.value }; setField('automobiles', a); }} /></div>
                ))}
              </div>
            ))}
            <SectionTitle>{lang === 'es' ? 'Acreedores de Vehículos' : 'Lienholders'}</SectionTitle>
            {[0, 1].map(i => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: 8, padding: '1rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ gridColumn: 'span 3', fontSize: '0.78rem', color: 'var(--neon-orange)', fontWeight: 600, textTransform: 'uppercase' }}>{`Lienholder ${i + 1}`}</span>
                {[{k:'name',en:'Lienholder',es:'Acreedor'},{k:'ins_co',en:'Ins. Co.',es:'Aseguradora'},{k:'phone',en:'Phone #',es:'Teléfono'}].map(f => (
                  <div key={f.k}><label className="form-label">{lang === 'es' ? f.es : f.en}</label>
                    <input className="form-input" value={(formData.lienholders || [])[i]?.[f.k] || ''} onChange={e => { const l = [...(formData.lienholders || [{},{}])]; while(l.length <= i) l.push({}); l[i] = { ...l[i], [f.k]: e.target.value }; setField('lienholders', l); }} /></div>
                ))}
              </div>
            ))}
          </div>
        );

      // ── Step 13: Review & Sign ──
      case 13:
        return (
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            <SectionTitle>{lang === 'es' ? 'Resumen de la Solicitud' : 'Application Summary'}</SectionTitle>
            <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 10, padding: '1.5rem', border: '1px solid rgba(255,255,255,0.06)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.88rem' }}>
              {[
                [lang === 'es' ? 'Nombre Completo' : 'Full Name', formData.full_name],
                [lang === 'es' ? 'Teléfono' : 'Phone', formData.telephone],
                [lang === 'es' ? 'Dirección' : 'Address', `${formData.address_street || ''} ${formData.address_city || ''}`],
                [lang === 'es' ? 'Acusado' : 'Defendant', formData.defendant_name],
                [lang === 'es' ? 'Cargo' : 'Charge', formData.charge],
                [lang === 'es' ? 'Monto Fianza' : 'Bond Amount', formData.bond_amount],
                [lang === 'es' ? 'Empleador' : 'Employer', formData.employer],
                [lang === 'es' ? 'Referencias' : 'References', `${(formData.references || []).filter(r => r.name).length} provided`],
              ].map(([label, val], i) => val ? (
                <div key={i} style={{ gridColumn: i % 2 === 0 ? 'span 1' : 'span 1' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 500, marginTop: 2 }}>{val}</div>
                </div>
              ) : null)}
            </div>

            <div style={{ background: 'rgba(255,107,0,0.04)', border: '1px solid rgba(255,107,0,0.15)', borderRadius: 8, padding: '1rem', fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
              <strong style={{ color: 'var(--warning)', display: 'block', marginBottom: '0.5rem' }}>
                {lang === 'es' ? 'POR FAVOR LEA ANTES DE FIRMAR:' : 'PLEASE READ CAREFULLY BEFORE SIGNING:'}
              </strong>
              {lang === 'es'
                ? 'El Cliente garantiza que las declaraciones y respuestas anteriores son verdad sin ninguna reserva, y se hacen con el propósito de inducir al Fiador a convertirse en fiador. El Cliente renuncia a su derecho con respecto a la Ley de Privacidad y autoriza el uso de copias de este documento.'
                : "The Client hereby warrants that the foregoing declarations made and answers given are the truth without any reservation, and are made for the purpose of inducing the Surety to become surety. The Client hereby waives their right with respect to the Privacy Act and authorizes the use of copies of this document by Steve Cruz Co. and its duly authorized representatives."}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={{ gridColumn: 'span 2' }}>
                <SignatureCanvas
                  label={lang === 'es' ? 'Firma del Cliente — X' : 'CLIENT SIGN HERE — X'}
                  onSignatureChange={setSignature}
                  required
                  height={140}
                />
                {errors.signature && <p className="form-error">{lang === 'es' ? 'Se requiere firma' : 'Signature required'}</p>}
              </div>
              <div>
                <label className="form-label">{lang === 'es' ? 'Dirección Postal' : 'Mailing Address'}</label>
                <input className="form-input" value={formData.mailing_address || formData.address_street || ''} onChange={e => setField('mailing_address', e.target.value)} />
              </div>
              <div>
                <label className="form-label">{lang === 'es' ? 'Fecha' : 'Date'}</label>
                <input className="form-input" type="date" value={formData.sign_date || new Date().toISOString().slice(0,10)} onChange={e => setField('sign_date', e.target.value)} />
              </div>
            </div>

            <div style={{ background: 'rgba(46,229,157,0.06)', border: '1px solid rgba(46,229,157,0.15)', borderRadius: 8, padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              <Shield size={15} color="var(--success)" style={{ flexShrink: 0 }} />
              {lang === 'es'
                ? 'Al enviar, se generarán 3 formularios PDF separados: Formulario de Referencias Personales, Solicitud de Fianza e Inventario de Activos.'
                : 'On submission, 3 separate PDF forms will be generated: Personal Reference Form, Surety Bond Application, and Asset/Collateral Inventory.'}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ── MAIN FORM LAYOUT ──────────────────────────────────────
  const currentStep = stepConfig[step - 1];

  return (
    <div style={{ background: 'var(--bg-0)', minHeight: '100vh' }}>
      <Helmet>
        <meta name="robots" content="noindex,nofollow,noarchive" />
      </Helmet>
      <SharedNav />
      <div style={{ paddingTop: '5rem', minHeight: '100vh' }}>
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '2rem 1.5rem' }}>
          {/* Header */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '0.75rem' }}>
              <div>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', textTransform: 'uppercase', lineHeight: 1 }}>
                  {lang === 'es' ? 'Solicitud de Fianza' : 'Bond Application'}
                </h1>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
                  {lang === 'es' ? `Paso ${step} de ${TOTAL_STEPS}` : `Step ${step} of ${TOTAL_STEPS}`} — {lang === 'es' ? currentStep?.es : currentStep?.en}
                </p>
              </div>
              <button onClick={handleSave} className="btn-outline" style={{ fontSize: '0.82rem', gap: '0.35rem', padding: '0.45rem 0.9rem' }}>
                <Save size={13} /> {lang === 'es' ? 'Guardar' : 'Save'}
              </button>
            </div>

            {/* Progress */}
            <div className="progress-bar-track">
              <motion.div className="progress-bar-fill" style={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
            </div>

            {/* Step dots */}
            <div style={{ display: 'flex', gap: '4px', marginTop: '0.6rem', flexWrap: 'wrap' }}>
              {stepConfig.map((s, i) => (
                <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: i < step ? 'var(--neon-orange)' : i === step - 1 ? 'var(--neon-orange)' : 'rgba(255,255,255,0.1)', boxShadow: i === step - 1 ? '0 0 6px rgba(255,107,0,0.6)' : 'none', flexShrink: 0 }} />
              ))}
            </div>
          </div>

          {/* Form Card */}
          <motion.div key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
            style={{ background: 'var(--surface-1)', border: '1px solid var(--border-steel)', borderRadius: 14, padding: '2rem', boxShadow: 'var(--shadow-panel)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.75rem', paddingBottom: '1.25rem', borderBottom: '1px solid var(--border-steel)' }}>
              <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(255,107,0,0.12)', border: '1px solid rgba(255,107,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--neon-orange)' }}>
                {currentStep?.icon}
              </div>
              <div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', textTransform: 'uppercase', lineHeight: 1 }}>
                  {lang === 'es' ? currentStep?.es : currentStep?.en}
                </h2>
              </div>
            </div>
            {renderStep()}
          </motion.div>

          {/* Navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', gap: '1rem' }}>
            <button onClick={prevStep} disabled={step === 1} className="btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', opacity: step === 1 ? 0.4 : 1 }}>
              <ArrowLeft size={15} /> {lang === 'es' ? 'Atrás' : 'Back'}
            </button>

            {step < TOTAL_STEPS ? (
              <button onClick={nextStep} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.95rem', fontFamily: 'var(--font-display)', letterSpacing: '0.06em' }} data-testid="next-step-btn">
                {lang === 'es' ? 'SIGUIENTE' : 'NEXT'} <ArrowRight size={15} />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={submitting} className="btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '1rem', fontFamily: 'var(--font-display)', letterSpacing: '0.08em', padding: '0.9rem 2rem', opacity: submitting ? 0.7 : 1 }} data-testid="submit-form-btn">
                {submitting ? (lang === 'es' ? 'ENVIANDO...' : 'SUBMITTING...') : (<><CheckCircle size={16} /> {lang === 'es' ? 'ENVIAR FORMULARIOS' : 'SUBMIT FORMS'}</>)}
              </button>
            )}
          </div>

          {/* License note */}
          <p style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '1.5rem' }}>
            Lone Star Bonding Co. | Steve Cruz | License No. 74629 | 1420 Washington Ave, Houston TX | (713) 228-8800
          </p>
        </div>
      </div>
    </div>
  );
};

export default IntakePage;
